import DOMPurify from 'dompurify';

/**
 * Tiny, self-contained markdown renderer. Intentionally minimal so the eval UI
 * has zero new dependencies and can be deleted cleanly after the eval period.
 * Output is passed through DOMPurify before rendering.
 *
 * Supported syntax:
 *   - ATX headings (# through ######)
 *   - Paragraphs / blank lines
 *   - Blockquotes (lines starting with "> ")
 *   - Unordered lists (lines starting with "- " or "* ")
 *   - Ordered lists (lines starting with "1. ")
 *   - Fenced code blocks (``` ... ```)
 *   - Inline code (`...`)
 *   - Bold (**...**), italic (*...* / _..._)
 *   - Links [text](url)
 *   - Automatic line breaks between non-empty paragraph lines
 */

const escapeHtml = (raw: string) =>
  raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const renderInline = (text: string): string => {
  let out = escapeHtml(text);
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>');
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  return out;
};

type Block = { html: string };

const flushList = (buffer: string[], ordered: boolean, out: Block[]) => {
  if (buffer.length === 0) return;
  const tag = ordered ? 'ol' : 'ul';
  const items = buffer.map((i) => `<li>${renderInline(i)}</li>`).join('');
  out.push({ html: `<${tag}>${items}</${tag}>` });
  buffer.length = 0;
};

const flushParagraph = (buffer: string[], out: Block[]) => {
  if (buffer.length === 0) return;
  const inner = buffer.map(renderInline).join('<br />');
  out.push({ html: `<p>${inner}</p>` });
  buffer.length = 0;
};

export const renderMarkdown = (input: string): string => {
  const lines = input.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];

  const paragraph: string[] = [];
  const bulletList: string[] = [];
  const numberList: string[] = [];
  let quote: string[] = [];
  let inCode = false;
  let codeLang = '';
  let codeBuffer: string[] = [];

  const flushAll = () => {
    flushParagraph(paragraph, blocks);
    flushList(bulletList, false, blocks);
    flushList(numberList, true, blocks);
    if (quote.length > 0) {
      const inner = quote.map(renderInline).join('<br />');
      blocks.push({ html: `<blockquote>${inner}</blockquote>` });
      quote = [];
    }
  };

  for (const raw of lines) {
    const line = raw.replace(/\t/g, '    ');

    const fenceMatch = line.match(/^```(\w*)\s*$/);
    if (fenceMatch) {
      if (inCode) {
        const langClass = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : '';
        blocks.push({ html: `<pre><code${langClass}>${escapeHtml(codeBuffer.join('\n'))}</code></pre>` });
        codeBuffer = [];
        codeLang = '';
        inCode = false;
      } else {
        flushAll();
        inCode = true;
        codeLang = fenceMatch[1] ?? '';
      }
      continue;
    }

    if (inCode) {
      codeBuffer.push(line);
      continue;
    }

    if (line.trim() === '') {
      flushAll();
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushAll();
      const level = heading[1].length;
      blocks.push({ html: `<h${level}>${renderInline(heading[2])}</h${level}>` });
      continue;
    }

    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph(paragraph, blocks);
      flushList(bulletList, false, blocks);
      flushList(numberList, true, blocks);
      quote.push(quoteMatch[1]);
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      flushParagraph(paragraph, blocks);
      flushList(numberList, true, blocks);
      bulletList.push(bullet[1]);
      continue;
    }

    const numbered = line.match(/^\d+\.\s+(.*)$/);
    if (numbered) {
      flushParagraph(paragraph, blocks);
      flushList(bulletList, false, blocks);
      numberList.push(numbered[1]);
      continue;
    }

    flushList(bulletList, false, blocks);
    flushList(numberList, true, blocks);
    if (quote.length > 0) {
      const inner = quote.map(renderInline).join('<br />');
      blocks.push({ html: `<blockquote>${inner}</blockquote>` });
      quote = [];
    }
    paragraph.push(line);
  }

  if (inCode) {
    const langClass = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : '';
    blocks.push({ html: `<pre><code${langClass}>${escapeHtml(codeBuffer.join('\n'))}</code></pre>` });
  }
  flushAll();

  const html = blocks.map((b) => b.html).join('\n');
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] });
};
